import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { AdminDashboard } from "./DashboardMockup";

export default function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMouse({ x, y });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative pt-24 sm:pt-36 lg:pt-44 pb-14 sm:pb-24 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 grid-bg" aria-hidden />
      <div
        className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] glow-vermilion opacity-40 pointer-events-none"
        style={{ transform: `translate(-50%, ${mouse.y * 8}px) translateX(${mouse.x * 12}px)` }}
        aria-hidden
      />
      <div className="absolute -bottom-40 left-0 w-[700px] h-[500px] glow-blue opacity-50 pointer-events-none" aria-hidden />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-8"
        >
          <div className="glass rounded-full px-3 py-1.5 inline-flex items-center gap-2 text-xs text-[#9BB8B8]" data-testid="hero-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4F00] pulse-dot shrink-0" />
            <span className="font-mono tracking-[0.12em] whitespace-nowrap">Early access</span>
            <span className="hidden sm:inline text-[#1F3535]">·</span>
            <span className="hidden sm:inline whitespace-nowrap">The Contractor Operations Platform</span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-headline"
          className="font-display text-center text-balance mx-auto max-w-5xl text-4xl sm:text-5xl lg:text-7xl xl:text-[88px] font-medium tracking-[-0.04em] leading-[1.05] sm:leading-[1.02]"
        >
          Run your entire <span className="italic font-normal text-[#9BB8B8]">contractor</span> operation
          <br className="hidden sm:block" /> from one place.
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="hero-subheadline"
          className="mt-7 text-center text-base sm:text-lg text-[#9BB8B8] max-w-2xl mx-auto leading-relaxed"
        >
          From onboarding and contracts to time tracking, payouts and compliance — nexus-vs keeps everything connected.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center"
        >
          <a
            href="https://nexus-vs.vawick.com/signin"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-3.5 rounded-full font-medium text-sm hover:bg-[#FF4F00] hover:text-white transition-all duration-300"
          >
            Request Demo
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#features"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-medium text-sm border border-[#152222] text-white hover:border-[#1F3535] hover:bg-[#0D1A1A] transition-all"
          >
            See Product
            <span className="text-[#4A6E6E] group-hover:text-white transition-colors">→</span>
          </a>
        </motion.div>

        {/* Dashboard mockup with floating cards */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block relative mt-20 lg:mt-24 max-w-[1100px] mx-auto"
          style={{ perspective: "2000px" }}
        >
          <div
            className="relative"
            style={{
              transform: `rotateX(${-mouse.y * 1.5}deg) rotateY(${mouse.x * 1.5}deg)`,
              transition: "transform 0.4s ease-out",
            }}
          >
            <AdminDashboard />


          </div>

          {/* Reflection */}
          <div
            className="absolute inset-x-12 -bottom-20 h-24 opacity-30 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at top, rgba(255,79,0,0.4), transparent 70%)",
              filter: "blur(40px)",
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
